// What a reviewer needs to decide on a delivery, read out of three answers:
//
//   GET /admin/tasks/{id}                 → TaskDetailPackage
//   GET /admin/tasks/{id}/review-rounds   → { task_id, items: [round…] }
//   GET /admin/events?task_id&kind=task.progress   (only with events:read)
//
// THE PACKAGE IS NOT THE TASK. `task` is one block of it; the acceptance
// criteria, the scope, the COMMITS, the reviews, `last_return`, the
// reservation and `where` (tier and repository, with its main branch) are its
// siblings. This screen used to keep `data.task` alone, which is why the card
// never showed a commit although every delivery carried them.
//
// THE ROUND IS WHAT IS DECIDED. Since T04 a delivery opens a review round that
// fixes the exact version (`base_commit` → `delivered_commit`), the criteria as
// they were at delivery, the AI's evaluation per criterion and the log. The
// decision cites that round by id; approving an unversioned one is refused.
//
// Pure functions: tested without a browser.

export const FINDING_LABEL = {
    cumple: 'Cumple',
    incumple: 'Incumple',
    no_comprobado: 'No comprobado',
};

// The round on the table: the open one. When there is none (a stale queue),
// the newest, so the card still shows what was delivered last.
export function currentRound(rounds) {
    const items = Array.isArray(rounds) ? rounds : [];
    return items.find(round => round.state === 'open') ?? items[items.length - 1] ?? null;
}

// EL HISTORIAL ES LA MISMA LECTURA, RONDA POR RONDA. La cola lee la ronda del
// momento; la pantalla de una tarea las lee todas. Una segunda función que
// armara los mismos campos con otros nombres terminaría contestando distinto
// sobre la misma fila — que es exactamente lo que `readRound` evita.
//
// Más nueva primero: lo que se quiere leer de un historial es lo último que
// pasó, y lo viejo está abajo.
export function readRounds(rounds) {
    const items = Array.isArray(rounds) ? rounds : [];
    return items
        .map(readRound)
        .filter(Boolean)
        .sort((a, b) => (b.number ?? 0) - (a.number ?? 0));
}

export function readRound(round) {
    if (!round) return null;

    const findings = Array.isArray(round.findings) ? round.findings : [];
    const byIndex = new Map(findings.map(finding => [finding.criterion_index, finding]));

    // Each criterion AS IT WAS in this round, with its evaluation if there is
    // one. `null` criteria means the round predates the snapshot: said, not
    // filled in with today's criteria.
    const criteria = Array.isArray(round.criteria)
        ? round.criteria.map(criterion => ({
            index: criterion.index,
            text: criterion.text,
            finding: byIndex.get(criterion.index) ?? null,
        }))
        : null;

    return {
        id: round.id,
        number: round.number,
        state: round.state,
        baseCommit: round.base_commit ?? null,
        deliveredCommit: round.delivered_commit ?? null,
        versioned: Boolean(round.versioned ?? (round.base_commit && round.delivered_commit)),
        deliveredBy: round.delivered_by ?? null,
        openedAt: round.opened_at ?? null,
        // LA DECISIÓN, que es de una persona con QA y no de la evaluación.
        // `decidedByAgent` existe porque el modelo lo registra: una decisión
        // tomada con una credencial de agente no se presenta como una revisión
        // independiente, y esconderlo sería presentarla así.
        decidedAt: round.decided_at ?? null,
        decidedBy: round.decided_by ?? null,
        decidedByAgent: Boolean(round.decided_by_agent),
        selfApproved: Boolean(round.self_approved),
        specRevision: round.spec_revision ?? null,
        criteria,
        criteriaKnown: Boolean(round.criteria_known ?? Array.isArray(round.criteria)),
        commits: Array.isArray(round.commits) ? round.commits : [],
        summary: round.summary ?? null,
        // The log, oldest first: an AI evaluation, a human note, the decision.
        entries: (Array.isArray(round.entries) ? round.entries : []).map(entry => ({
            id: entry.id,
            kind: entry.kind,
            authorId: entry.author_id ?? null,
            authorName: entry.author_name ?? null,
            tokenId: entry.author_token_id ?? null,
            body: entry.body ?? '',
            at: entry.created_at ?? null,
        })),
    };
}

export function readReviewCard({ detail, rounds, progress } = {}) {
    const pkg = detail ?? {};
    const task = {
        ...(pkg.task ?? {}),
        acceptance_criteria: pkg.acceptance_criteria ?? pkg.task?.acceptance_criteria ?? [],
        scope: pkg.scope ?? pkg.task?.scope ?? [],
    };

    const reservation = pkg.reservation ?? null;
    const reviews = Array.isArray(pkg.reviews) ? pkg.reviews : [];
    const returns = reviews
        .filter(review => review.result === 'returned')
        .map(review => ({
            id: review.id,
            at: review.reviewed_at ?? null,
            userId: review.reviewer_id ?? null,
            userName: review.reviewer_name ?? null,
            reason: review.reason ?? null,
        }));

    const round = readRound(currentRound(rounds));

    // The commits with author and date come from the package; the round only
    // carries hash and message. The package's are used when there are any.
    const packageCommits = Array.isArray(pkg.commits) ? pkg.commits : [];
    const commits = packageCommits.length ? packageCommits : (round?.commits ?? []);

    // What the agent reported while working: the notes of its heartbeats.
    const notes = (Array.isArray(progress) ? progress : [])
        .filter(event => event?.data?.note)
        .map(event => ({ id: event.id, at: event.createdAt ?? event.created_at ?? null, note: event.data.note }))
        .sort((a, b) => a.id - b.id);

    return {
        task,
        repository: pkg.where?.repository ?? null,
        tier: pkg.where?.tier ?? null,
        reservation: reservation
            ? {
                userId: reservation.by ?? null,
                userName: reservation.by_name ?? null,
                tokenId: reservation.token_id ?? null,
                session: reservation.session ?? null,
                since: reservation.since ?? null,
            }
            : null,
        commits,
        round,
        returns,
        lastReturn: pkg.last_return ?? null,
        notes,
        attempt: returns.length + 1,
    };
}

// Whether the round can be APPROVED from here, and if not, the sentence why.
// Returning is always possible: it is the way out of a round that cannot be
// approved.
export function approvalBlocker(round) {
    if (!round) return 'No hay una ronda de revisión abierta para esta tarea.';
    if (round.state !== 'open') return 'La ronda ya fue decidida.';
    if (!round.versioned) {
        return 'La entrega no fijó versión (commit base y entregado): no se puede aprobar, solo devolver.';
    }
    if (!round.criteriaKnown) {
        return 'La ronda no tiene los criterios fijados en la entrega: no se puede aprobar, solo devolver.';
    }
    return null;
}
